import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import DataGrid, { DataGridRef, Selection, Scrolling, Paging, FilterRow, LoadPanel as GridLoadPanel, SearchPanel, Column } from 'devextreme-react/data-grid'
import { MultiView, Item } from 'devextreme-react/multi-view'
import { useTranslation } from 'react-i18next'
import DropDownBox, { DropDownBoxRef } from 'devextreme-react/drop-down-box'
import { Button, ButtonRef } from 'devextreme-react/button'
import { IPopupOptions } from 'devextreme-react/popup'
import { useFormikContext, useField } from 'formik'
import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { Tabs, Tab } from 'components/tabs'
import { zeroPad } from 'helpers'
import { useIxOnScreen } from 'hooks/use-on-screen'
import { useHandleKbEventsCallback } from 'hooks'
import { isEqual } from 'lodash'
import LoadIndicator from 'devextreme-react/load-indicator'
import { IxTeamLookup } from 'components/forms/lookups/team-lookup.gen/team-lookup.gen'
import { IxUserLookup } from 'components/forms/lookups/user-lookup.gen/user-lookup.gen'
import { IxSupplierLookup } from 'components/forms/lookups/supplier-lookup.gen/supplier-lookup.gen'
import { Popup } from 'devextreme-react/popup'
import { useAppDispatch } from 'store'
import { useIxLookupContext } from 'contexts/lookup-context'
import useResizeObserver from '@react-hook/resize-observer'
import _ from 'lodash'

import CheckBox from 'devextreme-react/check-box'
import { useLabelWidth } from 'hooks/use-label-width'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import { BaseForm } from 'components'

import client from 'api'

// exported for testing
export const kbSwitchGrid = (currentLookup: any) => {

    return (e: any) => {
        if ((document.activeElement as any).getAttribute('aria-label') === 'Search in the data grid' && (document.activeElement as any).value) { return }

        if(e.key === 'ArrowRight') {
            const button: any = currentLookup.querySelector('[aria-label="chevronright"]')
            button.click()
        }
        if(e.key === 'ArrowLeft') {
            const button: any = currentLookup.querySelector('[aria-label="chevronleft"]')
            button.click()
        }

    }
}

const AutoUpdateForm = ({name, setLookup, setFiltersDataToStore, filtersDataFromStore}: any ) => {
    const { values, setFieldValue } = useFormikContext()
    const dispatch = useAppDispatch()
    const {lookupsValue: lookupsValueFromStore} = filtersDataFromStore || {}

    useEffect(() => {
        setLookup({value: (values as any)[name], name: name})
        setFiltersDataToStore && dispatch(setFiltersDataToStore({lookupsValue: values}))

    },[values, name, setLookup, dispatch, setFiltersDataToStore])

    useEffect(() => {
        if (lookupsValueFromStore) {
            Object.keys(lookupsValueFromStore).forEach((name: any) => {
                setFieldValue(name, lookupsValueFromStore[name])
            })
        }

    },[lookupsValueFromStore, setFieldValue])

    return null
}

const RenderDataGrid = ( { multiselect, name, setInFocus, selectedRows, dropdownbox, onSelectionChanged, onMultiSelectionChanged, handleGridReady, setFiltersDataToStore,
    visibleColumns, onRowClick, selectedRowKey, customizeColumns, url, setData, data, setSelectedRows, setRangeButtonValue, setActiveButtonId, setFieldValue,
    setNotExistValue, setValue, grid}: any ) => {
    const [field] = useField(name)
    const ref = useRef(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const onScreen = useIxOnScreen(ref)
    const dispatch = useAppDispatch()
    const { setFieldValue: setLookupFieldValue } = useIxLookupContext()

    const handleKbEventsCallback = useHandleKbEventsCallback(grid.current, dropdownbox.current, data)

    useEffect(() => {
        if(onScreen) {
            const currentGrid = grid.current
            const searchField: any = currentGrid?.instance()?.element().querySelector('.dx-item.dx-multiview-item.dx-item-selected .dx-icon.dx-icon-search + input')
            data.length && searchField?.focus()
            currentGrid?.instance()?.navigateToRow(selectedRowKey)

            if(!data.length) {
                setIsLoading(true)
                client.get(url).then((res) => {
                    setIsLoading(false)
                    setData(url.includes('ogr') ? res.data.filter((item: any) => (item.NR >= 0)) : res.data)
                })
            }
        }

    },[selectedRowKey, onScreen, data, url, setData, grid])

    useEffect(() => {
        const currentGrid = grid.current
        if (isLoading) {
            currentGrid?.instance()?.beginCustomLoading('')
        } else {
            currentGrid?.instance()?.endCustomLoading()
            }

    },[isLoading, grid])

    const handleRowDblClick = useCallback((e: any) => {
        if (multiselect) {
            const fieldValue = (document.querySelector(`[name=${name}][aria-expanded="true"]`) as any).value
            const selectedRowsNr: any = grid.current?.instance()?.getSelectedRowsData().map((row: any) => row.OBJNR)
            const valueWithDash = `${fieldValue}${(!fieldValue.endsWith('-') && !fieldValue.endsWith(',') && fieldValue ) ? ',' : ''}${selectedRowsNr?.length ? `${selectedRowsNr}` : ''}${!selectedRowsNr.includes(e.data.OBJNR) ? selectedRowsNr?.length ? `,${e.data.OBJNR}` : e.data.OBJNR : ''}`
            let rows: any = [...selectedRows]

            if (e.key && !selectedRows.find((rId: any) => rId === e.key)) {
                rows.push(e.key)
                setSelectedRows(rows)
            }
            if (setFiltersDataToStore) {
                dispatch(setFiltersDataToStore({objectRange: fieldValue.includes('-')
                    ? valueWithDash
                    : rows}))
                dispatch(setFiltersDataToStore({objectRangeValue: fieldValue.includes('-')
                    ? valueWithDash
                    : `${rows.map((id: any) => data.find((row: any) => row.HDR_ID === id).OBJNR)}`
                }))
            }
            else {
                setFieldValue(field.name,
                    fieldValue.includes('-')
                        ? valueWithDash
                        : `${rows.map((id: any) => data.find((row: any) => row.HDR_ID === id).OBJNR)}`, true)
                setValue(fieldValue.includes('-')
                    ? valueWithDash
                    : rows)
            }

            setLookupFieldValue(name, ({OBJNR:
                fieldValue.includes('-')
                    ? valueWithDash
                    : `${rows.map((id: any) => data.find((row: any) => row.HDR_ID === id).OBJNR)}`} as any)
            )
        }
        else {
            if (setFiltersDataToStore) {
                dispatch(setFiltersDataToStore({objectRange: e.data.HDR_ID}))
                dispatch(setFiltersDataToStore({objectRangeValue: e.data.BEZ}))
            }
            else {
                setFieldValue(field.name, e.data.BEZ, true)
                setValue(e.data.HDR_ID)
            }

            setLookupFieldValue(name, {...e.data, OBJNR: e.data.BEZ})
        }
        setRangeButtonValue('')
        setActiveButtonId(null)
        setFiltersDataToStore && dispatch(setFiltersDataToStore({activeButtonId: null}))
        setNotExistValue('')
        dropdownbox.current?.instance()?.close()
        setInFocus(false)

    }, [field.name, data, setFiltersDataToStore, setFieldValue, setInFocus, selectedRows, multiselect, dropdownbox, setSelectedRows, setNotExistValue, setRangeButtonValue, setActiveButtonId, dispatch, name, setLookupFieldValue, setValue, grid])

    return (
        <div style={{display: 'inline'}} ref={ref} {...!multiselect ? {onKeyDown: handleKbEventsCallback} : {}}>

            <DataGrid
                ref={grid}
                id='dropdown-grid'
                dataSource={data}
                height={multiselect ? '100%' : '81.7%'}
                repaintChangesOnly={true}
                showBorders={true}
                showColumnLines={true}
                onRowDblClick={handleRowDblClick}
                customizeColumns={customizeColumns}
                onContentReady={handleGridReady}
                {...!multiselect ? {onRowClick: onRowClick} : {}}
                columnAutoWidth={true}
                {...multiselect ? { onSelectionChanged: onMultiSelectionChanged, selectedRowKeys: selectedRows } : {onSelectionChanged: onSelectionChanged, selectedRowKeys: [selectedRowKey]}}
                keyExpr='HDR_ID'
                >
                <Selection mode={multiselect ? 'multiple' : 'single'} deferred={false} showCheckBoxesMode={ multiselect ? 'always' : 'none'} />
                <Scrolling mode='virtual' />
                <Paging enabled={true} pageSize={10} />
                <FilterRow visible={true} />
                <GridLoadPanel enabled={isLoading} />
                <SearchPanel searchVisibleColumnsOnly={true} visible={true}  defaultText={''}/>
                {visibleColumns.map((col: any) => <Column dataField={col} key={col}/> )}
            </DataGrid>
        </div>
    )
}

export const IxObjectRangeLookup = ({name, label, setFiltersDataToStore, filtersDataFromStore}: any) => {
    const {
        objectRange: objectRangeFromStore,
        usersData: usersDataFromStore,
        activeButtonId: activeButtonIdFromStore,
        userteamId: userteamIdFromStore,
        lookupName: lookupNameFromStore,
        isChecked: isCheckedFromStore,
        teamShortName: teamShortNameFromStore,
        objectRangeValue: objectRangeValueFromStore,
    } = filtersDataFromStore || {}
    const [selectedRow, setSelectedRow] = useState<any>(null)
    const [field] = useField(name)
    const { setFieldValue } = useFormikContext()
    const [open, setOpen] = useState(false)
    const [inFocus, setInFocus] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [selectedRows, setSelectedRows] = useState<any>([])
    const { paddings} = useSelector((state: RootState) => state.ui)
    const [objData, setObjData] = useState<any[]>([])
    const [objRangeData, setObjRangeData] = useState<any[]>([])
    const [activeObjectLookup, setActiveObjectLookup] = useState<number>(0)
    const [isChecked, setIsChecked] = useState<boolean>(false)
    const [isPopupResponsive, setIsPopupResponsive] = useState<boolean>(false)
    const [popupSize, setPopupWidth] = useState<any>({width: 1050, height: 750})
    const [notExistValue, setNotExistValue] = useState<any>('')
    const [teamLookupInfo, setTeamLookupInfo] = useState<any>({isVisible: false, selectedRow: false})
    const [userLookupInfo, setUserLookupInfo] = useState<any>({isVisible: false, selectedRow: false})
    const [userTeamLookupInfo, setUserTeamLookupInfo] = useState<any>({isVisible: false, selectedRow: false})
    const [supplierLookupInfo, setSupplierLookupInfo] = useState<any>({isVisible: false, selectedRow: false})
    const [lookupName, setLookupName] = useState<string>('none')
    const [lookup, setLookup] = useState<any>({value: null, name: ''})
    const [activeButtonId, setActiveButtonId] = useState<any>(null)
    const [rangeButtonValue, setRangeButtonValue] = useState('')
    const [lookupsValue, setLookupsValue] = useState<any>({shortName: '', userNbr: ''})
    const [warningVisible, setWarningVisible] = useState(false)
    const [userteamId, setUserteamId] = useState<any>(null)
    const [usersData, setUsersData] = useState<any []>([])
    const ref = useRef<any>(null)
    const onScreen = useIxOnScreen(ref)
    const dispatch = useAppDispatch()
    const { setFieldValue: setLookupFieldValue, addField, getFieldValue } = useIxLookupContext()
    const [value, setValue] = useState<any>(null)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const {value: openLookupOnClick} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.openLookupOnClick))
    const {value: ixCommonRuntime} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.ixCommonRuntime))
    const {value: number} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupNumberFormat))


    
    const [fieldWidth, setFieldWidth] = useState<any>(null)
    const getLabelWidth = useLabelWidth(formFieldType)
    const firstRender = useRef<any>(true)
    const okButton: any = useRef<ButtonRef>()
    const dropdownbox = useRef<DropDownBoxRef>(null)
    const objRange: any = useRef<DataGridRef>()
    const obj: any = useRef<DataGridRef>()
    const [searchText, setSearchText] = useState('')

    const { t } = useTranslation()

    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const logWidth = useCallback(() => {
        const refDropdownbox: any = dropdownbox.current
        const input: any = refDropdownbox?.instance()?.element().querySelector('.dx-texteditor-input-container')

        input && setFieldWidth(input.getBoundingClientRect().width)
    },[])

    const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((dropdownbox.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        isFloatingLabel && logWidth()
    },[logWidth, value, field.value, notExistValue, rangeButtonValue, isFloatingLabel])

    useEffect(() => {
        if (setFiltersDataToStore) {
            if (objectRangeFromStore && objectRangeValueFromStore) {
                setFieldValue(field.name, activeButtonIdFromStore ? objectRangeFromStore : objectRangeValueFromStore , true)
                setValue(objectRangeFromStore)
                !firstRender.current && setLookupFieldValue(field.name, objectRangeFromStore)

                if (!activeButtonIdFromStore) {
                    Array.isArray(objectRangeFromStore) ? setSelectedRows(objectRangeFromStore) : setSelectedRow(objRangeData.find((item: any) => item.BEZ === objectRangeValueFromStore))
                }
            }
            else if (!firstRender.current && (objectRangeFromStore === null && objectRangeValueFromStore === null)) {
                setFieldValue(field.name, null , true)
                setLookupFieldValue(field.name, null)
                setValue(null)
            }

        }

    },[objectRangeValueFromStore, objRangeData, objectRangeFromStore, setFiltersDataToStore, field.name, setFieldValue, setLookupFieldValue, firstRender, activeButtonIdFromStore])


    useEffect(() => {
        if (isFloatingLabel) {
            const refDropdownbox: any = dropdownbox.current
            const floatingLabel: HTMLElement = refDropdownbox?.instance()?.element().querySelector('.dx-label')
            const input: any = refDropdownbox?.instance()?.element().querySelector('input.dx-texteditor-input')

            if ((rangeButtonValue || notExistValue) || (setFiltersDataToStore ? field.value : value)) {
                const focusedFloatingLabel: HTMLElement = refDropdownbox?.instance()?.element().querySelector('.dx-texteditor-label .dx-label')

                floatingLabel && focusedFloatingLabel && (focusedFloatingLabel.style.left = `calc(100% - 60px - ${floatingLabel.offsetWidth-12}px)`)

                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)

                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+26)}px`)
                }

            }
            else {
                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)

                    floatingLabel.style.left = inFocus ? `calc(100% - 32px - ${floatingLabel.offsetWidth-12}px)` : '0px'
                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+16)}px`)
                }
            }
        }

    },[value, getLabelWidth, label, fieldWidth, field.value, notExistValue, rangeButtonValue, setFiltersDataToStore, inFocus, isFloatingLabel])

    const rangeBtns = useMemo(() => ([
        {id: '0', label: t('object:button.currentObject'), value: 'Aktuelles Objekt'},
        {id: '1', label: t('object:button.currentFIBU'), value: 'Aktuelle FIBU/Gruppe'},
        {id: '2', label: t('object:button.myObjects'), value: 'Meine Objekte'},
        {id: '3', label: t('object:button.mySubstitutionObjects'), value: 'Meine+vertretene Objekte'},
        {id: '4', label: t('object:button.objectsOf'), value: 'Objekte von Anwender'},
        {id: '5', label: t('object:button.myManagament'), value: 'Meine Verwaltung'},
        {id: '6', label: t('object:button.specialManagement'), value: 'Spezielle Verwaltung'},
        {id: '7', label: t('object:button.myObjectsTeam'), value: 'Meine Objekte'},
        {id: '8', label: t('object:button.mySubstitutionObjectsTeam'), value: 'Meine+vertretene Objekte'},
        {id: '9', label: t('object:button.objectsOfTeam'), value: 'Objekte von Anwender'}
    ]),[t])

    const tabs: Tab[] = useMemo(() => ([
        { name: '', icon: 'chevronleft' },
        { name: '', icon: 'chevronright' },
    ]),[])

    const customizeColumnsObj = useCallback((columns: any[]) => {
        columns.forEach(col => {
            if (col.dataField === 'OBJNR') {
                col.format = (x: any) => zeroPad(x, paddings['OBJNR'])
                col.caption = t('object:field.OBJNR')
            }
            if (col.dataField === 'O_ID') {col.caption = t('object:field.O_ID')}
            if (col.dataField === 'O_SRT') {col.caption = t('global:field.O_SRT')}
            if (col.dataField === 'O_ORT') {col.caption = t('global:field.O_ORT')}
        })

    }, [paddings, t])

    const customizeColumnsRangeObj = useCallback((columns: any[]) => {
        columns.forEach(col => {
            if (col.dataField === 'US') {col.caption = t('object:field.US')}
            if (col.dataField === 'BEZ') {col.caption = t('object:field.BEZ')}
            if (col.dataField === 'NR') {col.caption = t('object:field.NR')}
        })

    }, [t])

    useEffect(() => {
        !getFieldValue(name) && addField(name, null)
        const currentLookup = ref.current?.closest('.dx-overlay-content.dx-popup-normal.dx-resizable')
        if (onScreen) {
            currentLookup?.addEventListener('keydown', kbSwitchGrid(currentLookup))
        }

        return () => {
            currentLookup?.removeEventListener('keydown', kbSwitchGrid(currentLookup))
        }
    },[onScreen, addField, name, getFieldValue])

    const onActiveButton = useCallback((e: any) => {
        setFiltersDataToStore ? dispatch(setFiltersDataToStore({activeButtonId: e.element.id})) : setActiveButtonId(e.element.id)

        if (rangeBtns[e.element.id].label === t('object:button.myObjectsTeam') ||
            rangeBtns[e.element.id].label === t('object:button.mySubstitutionObjectsTeam') ||
            rangeBtns[e.element.id].label === t('object:button.objectsOfTeam')) {
                setTeamLookupInfo({isVisible: true, selectedRow: false})
                setFiltersDataToStore ? dispatch(setFiltersDataToStore({lookupName: 'TEAM'})) : setLookupName('TEAM')
        }
        else if (rangeBtns[e.element.id].label === t('object:button.objectsOf')) {
            setUserLookupInfo({isVisible: true, selectedRow: false})
            setFiltersDataToStore ? dispatch(setFiltersDataToStore({lookupName: 'USER'})) : setLookupName('USER')
        }
        else if (rangeBtns[e.element.id].label === t('object:button.specialManagement')) {
            setSupplierLookupInfo({isVisible: true, selectedRow: false})
            setFiltersDataToStore ? dispatch(setFiltersDataToStore({lookupName: 'SUPPLIER'})) : setLookupName('SUPPLIER')
        }
        else {
            setFiltersDataToStore ? dispatch(setFiltersDataToStore({lookupName: 'none'})) : setLookupName('none')
            dropdownbox.current?.instance()?.close()
        }

    },[rangeBtns, t, dispatch, setFiltersDataToStore])

    const onOkClick = useCallback((e: any) => {
        const fieldValue = (document.querySelector(`[name=${name}][aria-expanded="true"]`) as any)?.value
        const selectedRowsNr: any = e.singleVisibleRowData?.OBJNR || objRange.current?.instance()?.getSelectedRowsData().map((row: any) => row.OBJNR)
        const selectedRowsIds = e.singleVisibleRowData ? [...selectedRows, e.singleVisibleRowData.HDR_ID] : selectedRows
        const selectedRowData = e.singleVisibleRowData || selectedRow

        if ((selectedRowsIds.length || Array.isArray(value)) && activeObjectLookup === 1) {

            const valueWithDash = `${fieldValue}${(!fieldValue.endsWith('-') && !fieldValue.endsWith(',') && fieldValue && selectedRowsNr?.length) ? ',' : ''}${selectedRowsNr}`
            if (setFiltersDataToStore) {
                dispatch(setFiltersDataToStore({objectRange: fieldValue.includes('-')
                    ? valueWithDash
                    : selectedRowsIds}))
                dispatch(setFiltersDataToStore({objectRangeValue: fieldValue.includes('-')
                    ? valueWithDash
                    : `${selectedRowsIds.map((id: any) => objData.find((row: any) => row.HDR_ID === id).OBJNR)}`
                }))
            }
            else {
                setFieldValue(field.name,
                    fieldValue.includes('-')
                        ? valueWithDash
                        : `${selectedRowsIds.map((id: any) => objData.find((row: any) => row.HDR_ID === id).OBJNR)}`, true)
                setValue(fieldValue.includes('-')
                    ? valueWithDash
                    : selectedRowsIds)
            }

            setLookupFieldValue(name, {...e.data, OBJNR: fieldValue.includes('-')
                ? valueWithDash
                : `${selectedRowsIds.map((id: any) => objData.find((row: any) => row.HDR_ID === id).OBJNR)}`}
            )
        }
        if (selectedRowData || selectedRowsIds.length) {
            setRangeButtonValue('')
            setFiltersDataToStore
                ? dispatch(setFiltersDataToStore({activeButtonId: null}))
                : setActiveButtonId(null)

            if (selectedRowData && activeObjectLookup === 0) {
                if (setFiltersDataToStore) {
                    dispatch(setFiltersDataToStore({objectRange: selectedRowData.HDR_ID}))
                    dispatch(setFiltersDataToStore({objectRangeValue: selectedRowData.BEZ}))
                }
                else {
                    setFieldValue(field.name,  selectedRowData?.BEZ, true)
                    setValue(selectedRowData?.HDR_ID)
                }
                setLookupFieldValue(name, {...e.data, OBJNR: selectedRowData?.BEZ})
            }
        }

        setNotExistValue('')
        // setInFocus(false)
        dropdownbox.current?.instance()?.close()

    }, [selectedRow, field, setFieldValue, selectedRows, activeObjectLookup, dispatch,
        setFiltersDataToStore, setLookupFieldValue, name, objData, value,
        setNotExistValue, setRangeButtonValue, setActiveButtonId])

    const onRowClick = useCallback((e: any) => {
        if (e.data) {setSelectedRow(e.data)}
    },[])

    const handleGridReady = useCallback((e: any) => {
        if (activeObjectLookup === 0) {
            if (e.component.getVisibleRows().length === 1) {
                setSelectedRow(e.component.getVisibleRows()[0]?.data)

                if (!open) {
                    onOkClick({singleVisibleRowData: e.component.getVisibleRows()[0]?.data})
                    obj.current?.instance()?.clearFilter()
                    setSearchText('')
                }
            }
            else {
                if (searchText) {
                    setTimeout(() => { dropdownbox.current?.instance()?.open() }, 10)
                    e.element.querySelector('.dx-icon.dx-icon-search + input')?.focus()
                }
            }
        }

    }, [activeObjectLookup, onOkClick, open, searchText, setSearchText])

    useEffect(() => {
        if (setFiltersDataToStore) {
            client.get(`/api/ixweb/ogr/`).then((res) => {setObjRangeData(res.data.filter((item: any) => (item.NR >= 0)))})
            client.get(`/api/ixweb/obj/`).then((res) => {setObjData(res.data)})
        }

    },[setFiltersDataToStore])

    useEffect(() => {
        if (!objectRangeFromStore && setFiltersDataToStore && field.value) {
            if (Array.isArray(field.value)) {setSelectedRows([])}
            if(Number(field.value)) { setSelectedRow(null) }

            setNotExistValue(null)
        }

    },[objectRangeFromStore, field.value, setFiltersDataToStore])

    const getData = useCallback((type: any, valueToSet: any) => {
        setActiveObjectLookup(type === 'obj' ? 1 : 0)

        if (valueToSet.includes('-') && type === 'obj') {
            setFiltersDataToStore && dispatch(setFiltersDataToStore({objectRangeValue: valueToSet, objectRange: valueToSet}))
            setLookupFieldValue(name, ({OBJNR: valueToSet} as any))
            setFieldValue(field.name, valueToSet, true)
            setValue(valueToSet)
        }
        else {
            let existRow: any = null

            const _setValue = async(row: any, data: any) => {
                if (setFiltersDataToStore) {
                    dispatch(setFiltersDataToStore({objectRange: type === 'obj' ? row?.map((r: any) => r.HDR_ID) : row?.HDR_ID}))
                    dispatch(setFiltersDataToStore({objectRangeValue: type === 'obj' ? `${row.map((r: any) => data.find((item: any) => item.HDR_ID === r.HDR_ID)?.OBJNR)}` : row?.BEZ}))
                }
                else {
                    setFieldValue(field.name, (type === 'obj' ? `${row.map((r: any) => data.find((item: any) => item.HDR_ID === r.HDR_ID)?.OBJNR)}` : row?.BEZ), true)
                    setValue(type === 'obj' ? row?.map((r: any) => r.HDR_ID) :  row?.HDR_ID)
                    setLookupFieldValue(name, type === 'obj' ? row[0] : {OBJNR: row?.BEZ})
                }

                type === 'obj' ? setSelectedRows((_selectedRows: any) => { const newRows = row?.map((r: any) => r.HDR_ID); return _.isEqual(_selectedRows, newRows) ? _selectedRows : newRows }) : setSelectedRow(row)
            }

            const multiSearching = (_value: any, data: any) => {
                const objects: any[] = []
                _value?.split(',').map((el: any) => Number(el)).forEach((e: any) => {!objects.includes(Number(e)) && objects.push(e)})

                return objects?.map((obj: any) => data.find((item: any) => item.OBJNR === obj))
            }

            if (type === 'ogr' ? !objRangeData.length : !objData.length) {

                setIsLoading(true)
                client.get(`/api/ixweb/${type}/`).then((res: any) => {
                    setIsLoading(false)
                    existRow = type === 'obj' ? multiSearching(valueToSet, res.data) : res.data.find((item: any) => item.BEZ === valueToSet)
                    type === 'ogr' ? setObjRangeData(res.data.filter((item: any) => (item.NR >= 0))) : setObjData(res.data)

                    if (type === 'obj' ? (!existRow.includes(undefined) && existRow.length) : existRow) {
                        setNotExistValue('')
                        _setValue(existRow, res.data)
                    }
                    else {
                        setFiltersDataToStore ? dispatch(setFiltersDataToStore({objectRange: valueToSet, objectRangeValue: valueToSet})) : setNotExistValue(valueToSet)
                        setTimeout(() => {dropdownbox.current?.instance()?.focus()}, 100)
                        setInFocus(true)
                        type === 'ogr' && setTimeout(() => { obj?.current?.instance()?.searchByText(valueToSet) }, 0)
                        setSearchText(valueToSet)
                    }
                })
            }
            else {
                existRow = type === 'obj' ? multiSearching(valueToSet, objData) : objRangeData.find((item: any) => item.BEZ === valueToSet)

                if (type === 'obj' ? (!existRow.includes(undefined) && existRow.length) : existRow) {
                    setNotExistValue('')
                    _setValue(existRow, objData)
                    dropdownbox.current?.instance()?.blur()
                }
                else {
                    setFiltersDataToStore ? dispatch(setFiltersDataToStore({objectRange: valueToSet, objectRangeValue: valueToSet})) : setNotExistValue(valueToSet)
                    if (type === 'ogr' && !rangeButtonValue) {
                        setTimeout(() => { obj?.current?.instance()?.searchByText(valueToSet) }, 0)
                        setSearchText(valueToSet)
                    }
                }
            }
        }
        setInFocus(false)

    },[setFieldValue, field.name, objRangeData, objData, setNotExistValue, dispatch, setFiltersDataToStore, setLookupFieldValue, name, rangeButtonValue, setValue, setSearchText])

    useEffect(() => {
        if (!field.value) {
            setSelectedRows([])
            setSelectedRow(null)
        }
    },[field.value])

    const handleChange = useCallback((e: any) => {
        if (!e.event) { return }
        if (!e.value) {
            setActiveButtonId(null)
            setRangeButtonValue('')
            setNotExistValue('')
            setLookupFieldValue(field.name, null)
            setFiltersDataToStore ? dispatch(setFiltersDataToStore({objectRange: null})) : setFieldValue(field.name, null, true)
            setFiltersDataToStore && dispatch(setFiltersDataToStore({activeButtonId: null}))
            setFiltersDataToStore && dispatch(setFiltersDataToStore({objectRangeValue: null}))
            setValue(null)
        }
        else {
            setRangeButtonValue('')
            setActiveButtonId(null)
            setFiltersDataToStore && dispatch(setFiltersDataToStore({activeButtonId: null}))
            getData(!isNaN(Number(e.value[0])) ? 'obj' : 'ogr', e.value)

        }

    }, [setFieldValue, getData, dispatch, setFiltersDataToStore, setLookupFieldValue, field.name])

    const handlePopupHiding = useCallback((e: any) => {
        setTeamLookupInfo((teamLookupInfo: any) => ({...teamLookupInfo, isVisible: false}))
        setUserLookupInfo((userLookupInfo: any) => ({...userLookupInfo, isVisible: false}))
        setUserTeamLookupInfo((userTeamLookupInfo: any) => ({...userTeamLookupInfo, isVisible: false}))
        setSupplierLookupInfo((supplierLookupInfo: any) => ({...supplierLookupInfo, isVisible: false}))

        dropdownbox.current?.instance()?.close()
        setTimeout(() => {dropdownbox.current?.instance()?.focus()}, 0)
        obj.current?.instance()?.clearFilter()
        setSearchText('')

    }, [setSearchText, dropdownbox, setTeamLookupInfo, setUserLookupInfo, setUserTeamLookupInfo, setSupplierLookupInfo])

    const handlePopupShowing = useCallback((e: any) => {
        const fieldValue = (document.querySelector(`[name=${name}][type="text"]`) as any)?.value
        if (fieldValue.includes('-') && activeObjectLookup === 1) {setSelectedRows([])}
        if (((Array.isArray(field.value) && !field.value.length) || !field.value) && selectedRows.length) {
            setSelectedRows([])
        }
        if (!setFiltersDataToStore) {
            const value: any =  e.element.parentElement.querySelector('.dx-texteditor-input-container input').value
            value && getData(!isNaN(Number(value[0])) ? 'obj' : 'ogr', value)
        }

        setInFocus(true)
        e.component.content().parentNode.classList.add('dropdownbox-input-overlay')
    }, [getData, field.value, selectedRows, activeObjectLookup, name, setFiltersDataToStore])

    const handleResize = useCallback((e: any) => {
        setIsPopupResponsive(matchMedia(`(max-width: ${popupSize.width}px)`).matches)
    },[popupSize])

    useEffect(() => {
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [handleResize])

    const dropDownOptions: IPopupOptions = useMemo(() => ({
        minWidth: '176px',
        width: isPopupResponsive ? '100vw' : `${popupSize.width}px`,
        height: popupSize.height,
        minHeight: '550px',
        dragEnabled: true,
        position: open ? { at: 'left bottom', my: 'left top', collision: 'flipfit', of: dropdownbox.current?.instance()?.field() } : {},
        resizeEnabled: true,
        onResizeEnd: (e) => {setPopupWidth({width: e.width, height: e.height} )},
        showCloseButton: false,
        onShowing: handlePopupShowing,
        onHiding: handlePopupHiding,
        title: t('global:field.OBJGRUPPE'),
        showTitle: true,
        disabled: false,
        hideOnOutsideClick: false,
        toolbarItems: [
            {
                toolbar: 'top',
                location: 'after',
                widget: 'dxButton',
                disabled: false,
                options: {
                    type: 'close',
                    icon: 'close',
                    onClick: () => {
                        dropdownbox.current?.instance()?.close()
                        // setTimeout(() => {dropdownbox.current?.instance()?.focus()}, 0)
                    }
                }
            }
        ],
    }), [handlePopupShowing, handlePopupHiding, isPopupResponsive, popupSize, open, t])

    const _onSelectionChanged = useCallback((e: any) => {
        e.selectedRowsData.length && setSelectedRow(e.selectedRowsData[0])
    },[])

    const _onMultiSelectionChanged = useCallback((e: any) => {
        setSelectedRows((selectedRows: any[]) => {
            let _selectedRows = selectedRows.filter((r: any) => e.currentDeselectedRowKeys.indexOf(r) === -1)
            if (!isEqual(_selectedRows, e.currentSelectedRowKeys)) {
                _selectedRows = _selectedRows.concat(e.currentSelectedRowKeys)
            }
            return _selectedRows
        })


    }, [])

    useEffect(() => {
        (usersData.length && setFiltersDataToStore) && dispatch(setFiltersDataToStore({usersData: usersData.filter((x) => x)}))
    },[usersData, dispatch, setFiltersDataToStore])

    useEffect(() => {

        if (rangeBtns[activeButtonIdFromStore || activeButtonId]?.label === t('object:button.objectsOfTeam') && (teamShortNameFromStore || lookupsValue.shortName)) {
            const buttonValue: any = rangeBtns[activeButtonIdFromStore || activeButtonId]?.value

            if (setFiltersDataToStore) {
                dispatch(setFiltersDataToStore({objectRange: `${buttonValue}${
                    usersDataFromStore?.find((user: any) => user.USER_ID === lookupsValue.userNbr)
                            ? ` ${lookupsValue.userNbr}`
                            : ''
                    } (${teamShortNameFromStore}${(isCheckedFromStore) ? ',F:V' : ''})`}))
                dispatch(setFiltersDataToStore({objectRangeValue: `${buttonValue}${
                    usersDataFromStore?.find((user: any) => user.USER_ID === lookupsValue.userNbr)
                            ? ` ${lookupsValue.userNbr}`
                            : ''
                    } (${teamShortNameFromStore}${(isCheckedFromStore) ? ',F:V' : ''})`}))
            }
            else {
                setRangeButtonValue(`${buttonValue}${usersData?.find((user: any) => user.USER_ID === lookupsValue.userNbr) ? ` ${lookupsValue.userNbr}` : ''} (${lookupsValue.shortName}${(isChecked) ? ',F:V' : ''})`)
            }
        }

    },[lookupsValue, teamShortNameFromStore, setFiltersDataToStore, activeButtonId,
        usersDataFromStore, rangeBtns, t, isChecked, usersData, dispatch,
        activeButtonIdFromStore, isCheckedFromStore])

    useEffect(() => {
        if (!teamLookupInfo.isVisible && teamLookupInfo.selectedRow?.SHORTNAME && rangeBtns[activeButtonIdFromStore || activeButtonId].label === t('object:button.objectsOfTeam')) {
            setTimeout(() => {
                setFiltersDataToStore ? dispatch(setFiltersDataToStore({lookupName: 'USER_TEAM'})) : setLookupName('USER_TEAM')
                setTeamLookupInfo({isVisible: false, selectedRow: false})
                setUserTeamLookupInfo({isVisible: true, selectedRow: false})
                setFiltersDataToStore ? dispatch(setFiltersDataToStore({userteamId: teamLookupInfo.selectedRow.HDR_ID})) : setUserteamId(teamLookupInfo.selectedRow.HDR_ID)
            }, 100)
        }
        else if (teamLookupInfo.selectedRow && !teamLookupInfo.selectedRow.SHORTNAME) {
            setWarningVisible(true)
            setTeamLookupInfo({isVisible: true, selectedRow: false})
        }
    },[teamLookupInfo, setFiltersDataToStore, activeButtonId, t, rangeBtns, activeButtonIdFromStore, dispatch])

    useEffect(() => {
        if (teamLookupInfo.selectedRow && !teamLookupInfo.selectedRow.SHORTNAME && rangeBtns[activeButtonIdFromStore || activeButtonId]?.label !== t('object:button.objectsOfTeam')) {
            setWarningVisible(true)
            return
        }

        if ((teamLookupInfo.selectedRow?.SHORTNAME && rangeBtns[activeButtonIdFromStore || activeButtonId].label !== t('object:button.objectsOfTeam')) ||
            (supplierLookupInfo.selectedRow) ||
            (userLookupInfo.selectedRow)) {
                dropdownbox.current?.instance()?.close()
                setTeamLookupInfo({isVisible: false, selectedRow: false})
                setSupplierLookupInfo({isVisible: false, selectedRow: false})
                setUserLookupInfo({isVisible: false, selectedRow: false})
        }

    },[teamLookupInfo.selectedRow, supplierLookupInfo.selectedRow, userLookupInfo.selectedRow,
        rangeBtns, activeButtonId, t, activeButtonIdFromStore])


    useEffect(() => {
        if ((rangeBtns[activeButtonIdFromStore || activeButtonId]?.label === t('object:button.myObjectsTeam') ||
            rangeBtns[activeButtonIdFromStore || activeButtonId]?.label === t('object:button.mySubstitutionObjectsTeam')) &&
            !teamLookupInfo.isVisible && teamLookupInfo.selectedRow?.SHORTNAME
            ) {
                if (setFiltersDataToStore) {
                    userteamIdFromStore !== teamLookupInfo.selectedRow.HDR_ID && dispatch(setFiltersDataToStore({userteamId: teamLookupInfo.selectedRow.HDR_ID}))
                }
                else {
                    userteamId !== teamLookupInfo.selectedRow.HDR_ID && setUserteamId(teamLookupInfo.selectedRow.HDR_ID)
                }
        }

    },[dispatch, t, setFiltersDataToStore, teamLookupInfo, rangeBtns, activeButtonIdFromStore, activeButtonId, userteamId, userteamIdFromStore])

    useEffect(() => {
        if (teamLookupInfo.selectedRow?.SHORTNAME) {
            setFiltersDataToStore ? dispatch(setFiltersDataToStore({teamShortName: teamLookupInfo.selectedRow.SHORTNAME})) : setLookupsValue((lookupsValue: any) => ({...lookupsValue, shortName: teamLookupInfo.selectedRow.SHORTNAME}))
        }

    },[dispatch, teamLookupInfo.selectedRow, teamLookupInfo, setFiltersDataToStore])

    useEffect(() => {
        const buttonValue: any = rangeBtns[activeButtonIdFromStore || activeButtonId]?.value
        if (lookup.value) {
            if (rangeBtns[activeButtonIdFromStore || activeButtonId]?.label !== t('object:button.objectsOfTeam')) {
                if(buttonValue) {
                    if (setFiltersDataToStore) {
                        dispatch(setFiltersDataToStore({
                            objectRange: lookup.name === 'TEAM'
                                ? `${buttonValue} (${teamShortNameFromStore}${(isCheckedFromStore) ? ',F:V' : ''})`
                                : `${buttonValue} ${lookup.value}${(isCheckedFromStore) ? ' (F:V)' : ''}`
                        }))
                        dispatch(setFiltersDataToStore({
                            objectRangeValue: lookup.name === 'TEAM'
                                ? `${buttonValue} (${teamShortNameFromStore}${(isCheckedFromStore) ? ',F:V' : ''})`
                                : `${buttonValue} ${lookup.value}${(isCheckedFromStore) ? ' (F:V)' : ''}`
                        }))
                    }
                    else {
                        setRangeButtonValue(lookup.name === 'TEAM' ? `${buttonValue} (${lookupsValue.shortName}${(isChecked) ? ',F:V' : ''})` : `${buttonValue} ${lookup.value}${(isChecked) ? ' (F:V)' : ''}`)
                        setFieldValue(field.name, lookup.name === 'TEAM' ? `${buttonValue} (${lookupsValue.shortName}${(isChecked) ? ',F:V' : ''})` : `${buttonValue} ${lookup.value}${(isChecked) ? ' (F:V)' : ''}`, true)
                        setLookupFieldValue(field.name, lookup.name === 'TEAM' ? `${buttonValue} (${lookupsValue.shortName}${(isChecked) ? ',F:V' : ''})` : `${buttonValue} ${lookup.value}${(isChecked) ? ' (F:V)' : ''}`)
                        setValue(null)
                    }
                }
                if (lookup.name === 'USER') {
                    setLookupsValue((lookupsValue: any) => ({...lookupsValue, userNbr: lookup.value}))
                    setFiltersDataToStore ? dispatch(setFiltersDataToStore({userteamId: 0})) : setUserteamId(0)
                }
            }
            else {
                if (lookup.name === 'USER_TEAM') { setLookupsValue((lookupsValue: any) => ({...lookupsValue, userNbr: lookup.value})) }
            }
        }
        else {
            if (rangeBtns[activeButtonIdFromStore || activeButtonId]?.label === t('object:button.objectsOfTeam') && lookup.name === 'USER_TEAM') { return }
            if (rangeBtns[activeButtonIdFromStore || activeButtonId]?.label === t('object:button.objectsOf') && lookup.name === 'USER') { setFiltersDataToStore ? dispatch(setFiltersDataToStore({userteamId: 0})) : setUserteamId(0) }
            if (buttonValue) {
                setNotExistValue(null)

                if(setFiltersDataToStore) {
                    if (buttonValue === 'Aktuelles Objekt' || buttonValue === 'Aktuelle FIBU/Gruppe') {
                        dispatch(setFiltersDataToStore({objectRange: `${buttonValue}(${buttonValue === 'Aktuelle FIBU/Gruppe' ? 'von Objekt ' : ''}${ixCommonRuntime.objNr})`}))
                        dispatch(setFiltersDataToStore({objectRangeValue: buttonValue === 'Aktuelle FIBU/Gruppe' ? `##Gruppe##${ixCommonRuntime.objNr}` : ixCommonRuntime.objNr}))
                    }
                    else {
                        dispatch(setFiltersDataToStore({objectRange: `${buttonValue}${(isCheckedFromStore || isChecked) ? ' (F:V)' : ''}`}))
                        dispatch(setFiltersDataToStore({objectRangeValue: `${buttonValue}${(isCheckedFromStore || isChecked) ? ' (F:V)' : ''}`}))
                    }
                }
                else {
                    if (buttonValue === 'Aktuelles Objekt' || buttonValue === 'Aktuelle FIBU/Gruppe') {
                        setRangeButtonValue(`${buttonValue}(${buttonValue === 'Aktuelle FIBU/Gruppe' ? 'von Objekt ' : ''}${ixCommonRuntime.objNr})`)
                        setFieldValue(field.name, buttonValue === 'Aktuelle FIBU/Gruppe' ? `##Gruppe##${ixCommonRuntime.objNr}` : ixCommonRuntime.objNr, true)
                        setLookupFieldValue(field.name, `${buttonValue}(${buttonValue === 'Aktuelle FIBU/Gruppe' ? 'von Objekt ' : ''}${ixCommonRuntime.objNr})`)
                        setValue(null)
                    }
                    else{
                        setRangeButtonValue(`${buttonValue}${(isCheckedFromStore || isChecked) ? ' (F:V)' : ''}`)
                        setFieldValue(field.name, `${buttonValue}${(isCheckedFromStore || isChecked) ? ' (F:V)' : ''}`, true)
                        setLookupFieldValue(field.name, `${buttonValue}${(isCheckedFromStore || isChecked) ? ' (F:V)' : ''}`)
                        setValue(null)
                    }
                }
            }
        }

    },[lookup.name, lookup.value, t, setFiltersDataToStore, rangeBtns, activeButtonId, isChecked,
        field.name, setFieldValue, dispatch, activeButtonIdFromStore, isCheckedFromStore,
        teamShortNameFromStore, lookupsValue.shortName, ixCommonRuntime.objNr, setLookupFieldValue])


    const formik = useMemo(() => ({
        initialValues: {
            USER: null,
            TEAM: null,
            SUPPLIER: null
        },
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },
    }), [])

    const contentRender = useCallback((_: any) => {
        return (
            <BaseForm {...formik}>
                <div style={{display: 'inline'}} ref={ref}>
                    <Tabs tabs={tabs} onTabClick={(i) => setActiveObjectLookup(i)} activeTabIndex={activeObjectLookup} />
                    <AutoUpdateForm name={lookupNameFromStore || lookupName} setLookup={setLookup} setFiltersDataToStore={setFiltersDataToStore} filtersDataFromStore={filtersDataFromStore}/>
                    <MultiView
                        selectedIndex={activeObjectLookup}

                        swipeEnabled={false}
                        focusStateEnabled={false}
                        deferRendering={true}
                        height='calc(100% - 90px)'
                    >
                        <Item>
                            <div style={{margin: '0 0 10px 5px'}}>
                                <CheckBox text='Verwaltungsende berücksichtigen' value={isCheckedFromStore || isChecked} onValueChanged={(e) => setFiltersDataToStore ? dispatch(setFiltersDataToStore({isChecked: e.value})) : setIsChecked(e.value)} />
                            </div>
                            <IxTeamLookup name='TEAM' readOnly={false} visible={false} openDropdown={teamLookupInfo.isVisible} onInfoChange={setTeamLookupInfo} />
                            <IxUserLookup name='USER' readOnly={false} visible={false} openDropdown={userLookupInfo.isVisible} onInfoChange={setUserLookupInfo}/>
                            <IxUserLookup name='USER_TEAM' readOnly={false} visible={false} openDropdown={userTeamLookupInfo.isVisible} onInfoChange={setUserTeamLookupInfo} userteamId={userteamIdFromStore || userteamId} setUsersData={setUsersData}/>
                            <IxSupplierLookup name='SUPPLIER' readOnly={false} visible={false} openDropdown={supplierLookupInfo.isVisible} onInfoChange={setSupplierLookupInfo}/>
                            <div>
                                {
                                    rangeBtns.map((btn: any) => (
                                            <Button elementAttr={{style: 'margin: 0 0 10px 10px'}} text={btn.label} id={btn.id} onClick={onActiveButton} key={btn.id} hint={btn.label}/>
                                    ))
                                }
                            </div>

                            <RenderDataGrid
                                data={objRangeData}
                                name={name}
                                dropdownbox={dropdownbox}
                                visibleColumns={['BEZ', 'NR', 'US']}
                                onSelectionChanged={_onSelectionChanged}
                                setInFocus={setInFocus}
                                onRowClick={onRowClick}
                                selectedRowKey={selectedRow?.HDR_ID}
                                customizeColumns={customizeColumnsRangeObj}
                                url='/api/ixweb/ogr/'
                                setData={setObjRangeData}
                                setNotExistValue={setNotExistValue}
                                setRangeButtonValue={setRangeButtonValue}
                                setActiveButtonId={setActiveButtonId}
                                setFiltersDataToStore={setFiltersDataToStore}
                                handleGridReady={handleGridReady}
                                setValue={setValue}
                                grid={obj}
                                setFieldValue={setFieldValue}
                            />
                        </Item>

                        <Item>
                            <RenderDataGrid
                                data={objData}
                                multiselect={true}
                                name={name}
                                setInFocus={setInFocus}
                                selectedRows={selectedRows}
                                dropdownbox={dropdownbox}
                                onMultiSelectionChanged={_onMultiSelectionChanged}
                                visibleColumns={['OBJNR', 'O_ID', 'O_SRT', 'O_ORT']}
                                setSelectedRows={setSelectedRows}
                                customizeColumns={customizeColumnsObj}
                                url='/api/ixweb/obj/'
                                setData={setObjData}
                                setNotExistValue={setNotExistValue}
                                setRangeButtonValue={setRangeButtonValue}
                                setActiveButtonId={setActiveButtonId}
                                setFiltersDataToStore={setFiltersDataToStore}
                                handleGridReady={handleGridReady}
                                setValue={setValue}
                                grid={objRange}
                                setFieldValue={setFieldValue}
                            />
                        </Item>
                    </MultiView>

                    <div className='confirm-btns'>
                        <Button text={t('global:button.ok')} type='default' id='asd' onClick={onOkClick} />
                        <Button text={t('global:button.cancel')} type='default' onClick={handlePopupHiding} />
                    </div>


                    <Popup
                        visible={warningVisible}
                        showTitle={true}
                        title={t('global:label.warning')}
                        showCloseButton={false}
                        width={300}
                        height='auto'
                        className='confirm-popup'
                        onShown={() => {okButton.current?.instance()?.focus()}}
                    >
                        <div className='confirm-popup--content'>
                            <p style={ { marginTop: '0.25rem' } }>{t('object:message.warning')}</p>
                            <div className='confirm-popup--btns'>
                                <Button ref={okButton} text={t('global:button.ok')} type='default' stylingMode='contained' onClick={() => {setWarningVisible(false)}} />
                            </div>
                        </div>
                    </Popup>
                </div>
            </BaseForm>


        )
    }, [activeObjectLookup, formik, filtersDataFromStore, lookupName, lookupNameFromStore, handlePopupHiding, name, objData, objRangeData, onRowClick, customizeColumnsObj, warningVisible, userTeamLookupInfo.isVisible, teamLookupInfo.isVisible, userLookupInfo.isVisible, dispatch, handleGridReady, setFiltersDataToStore,
        onActiveButton, rangeBtns, selectedRows, onOkClick, t, tabs, _onSelectionChanged, selectedRow, customizeColumnsRangeObj, supplierLookupInfo.isVisible, userteamId, userteamIdFromStore, isCheckedFromStore, isChecked, _onMultiSelectionChanged, setFieldValue
    ])

    const onFocusOut = useCallback(() => {
        if (open) {return}
        setInFocus(false)
    }, [open])

    const onFocusIn = useCallback(() => {
        setInFocus(true)
    }, [])

    const onOptionChanged = (e: any) => {
        if (e.name === 'opened') {
            setOpen(e.value)
        }
    }

    const valueFormatter = useCallback((val: any) => {
        if (Array.isArray(value) && !notExistValue) {
            if (setFiltersDataToStore) {
                return inFocus
                    ? zeroPad(val.OBJNR, paddings['OBJNR'])
                    : `${number.prefix}${zeroPad(val.OBJNR, paddings['OBJNR'])}${value.length <= 3 ? `+${val.O_ID}` : ''}`
            }
            else {
                const row: any = objData.find((row: any) => row.HDR_ID === val.HDR_ID)
                return inFocus
                    ? zeroPad(row?.OBJNR, paddings['OBJNR'])
                    : `${number.prefix}${zeroPad(row?.OBJNR, paddings['OBJNR'])}${value.length <= 3 ? `+${row?.O_ID}` : ''}`
            }

        }

        else { return val.BEZ }

    },[inFocus, number, paddings, value, notExistValue, objData, setFiltersDataToStore])

    const ddButtons = useMemo<any>(() => ([
        { name: 'clear' },
        {
            location: 'after',
            name: 'dropDown',
        },
        {
            location: 'before',
            name: 'label',
            options: {
                text: label,
                stylingMode: 'text',
                hoverStateEnabled: false,
                focusStateEnabled: false,
                activeStateEnabled: false,
                elementAttr: {
                    class: 'label-inside-field'
                }
            }
        }
    ]), [label])

    useEffect(()=> {
        if (field.value && firstRender.current) {
            getData(!isNaN(Number(field.value[0])) ? 'obj' : 'ogr', field.value)
            firstRender.current = false
        }
        firstRender.current = false
    }, [field.value, firstRender, getData])

    return (
        <div className='rb-item mb-1'>
            <div>
                <label className='dx-field-label'>{label}</label>
                {
                    isLoading
                    ? (
                        <div className='base-lookup--loader'>
                            <LoadIndicator height={16} width={16} />
                        </div>
                    )
                    : (
                        <DropDownBox
                            ref={dropdownbox}
                            label={label}
                            labelMode= 'floating'
                            buttons={ddButtons}
                            dataSource={Array.isArray(value) ? objData : objRangeData}
                            contentRender={contentRender}
                            dropDownOptions={dropDownOptions}
                            placeholder={t('global:label.select')}
                            value={(rangeButtonValue || notExistValue) || value}
                            showClearButton={rangeButtonValue || notExistValue || value}
                            onValueChanged={handleChange}
                            className='dx-field-input'
                            openOnFieldClick={openLookupOnClick}
                            acceptCustomValue={true}
                            onOptionChanged={onOptionChanged}
                            onFocusIn={onFocusIn}
                            onFocusOut={onFocusOut}
                            readOnly={open}
                            deferRendering={false}
                            opened={open}
                            inputAttr={{name: name}}
                            displayExpr={valueFormatter}
                            valueExpr='HDR_ID'
                        />
                    )
                }
            </div>
        </div>
    )
}
